import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import SteerCard from '../components/you/SteerCard';
import AddNumber from '../components/you/AddNumber';
import TryThis from '../components/you/TryThis';
import SupplementIdeasRow from '../components/you/SupplementIdeasRow';
import DialTargetEditor from '../components/home/DialTargetEditor';
import apiClient from '../utils/api';

// You — the numbers you're steering (2026-10 restyle). A grid of goal cards,
// one tap each to change the goal; a tile to add another; then a short menu
// of things to try and one row of supplement ideas. Everything else about a
// number lives on the Data page.

const YouPage = () => {
  const [cards, setCards] = useState(null);
  const [byKey, setByKey] = useState(new Map());
  const [targets, setTargets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [openKey, setOpenKey] = useState(null);

  // Stale AI tips regenerate in the background; the cards never wait on them.
  const refreshSuggestions = useCallback((list) => {
    if (!list?.some((c) => c.suggestion_stale)) return;
    apiClient.post('/api/twin/cards/suggestions')
      .then((res) => setCards((prev) => prev
        ? prev.map((c) => (c.metric_key in res.data ? { ...c, suggestion: res.data[c.metric_key], suggestion_stale: false } : c))
        : prev))
      .catch(() => {});
  }, []);

  const load = useCallback(async () => {
    try {
      const [cardRes, trajRes, targetRes] = await Promise.all([
        apiClient.get('/api/twin/cards'),
        apiClient.get('/api/twin/trajectory'),
        apiClient.get('/api/twin/targets'),
      ]);
      setCards(cardRes.data);
      setByKey(new Map(trajRes.data.map((tr) => [tr.metric_key, tr])));
      setTargets(targetRes.data);
      refreshSuggestions(cardRes.data);
    } catch (error) {
      toast.error('Could not load your numbers');
    } finally {
      setLoading(false);
    }
  }, [refreshSuggestions]);

  useEffect(() => { load(); }, [load]);

  const handleTargetSaved = useCallback((updated) => {
    setTargets((prev) => prev.map((t) => (t.metric_key === updated.metric_key ? updated : t)));
    load();
  }, [load]);

  const setPin = async (metricKey, pinned) => {
    setBusy(true);
    try {
      await apiClient.patch(`/api/twin/cards/${metricKey}`, { pinned });
      const cardRes = await apiClient.get('/api/twin/cards');
      setCards(cardRes.data);
      setTargets((prev) => prev.map((t) => (t.metric_key === metricKey ? { ...t, pinned } : t)));
      if (pinned) refreshSuggestions(cardRes.data);
      if (!pinned) setOpenKey(null);
    } catch (error) {
      toast.error('Could not change that');
    } finally {
      setBusy(false);
    }
  };

  const available = useMemo(() => targets.filter((t) => !t.pinned && t.enabled), [targets]);
  const targetByKey = useMemo(() => new Map(targets.map((t) => [t.metric_key, t])), [targets]);
  const n = cards?.length || 0;
  const count = ['No', 'One', 'Two', 'Three', 'Four', 'Five', 'Six'][n] || String(n);

  return (
    <div className="mx-auto max-w-[1080px] px-4 sm:px-6 lg:px-10">
      <div className="mb-[22px] flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-[1.75rem] font-medium leading-[1.15] tracking-[-0.02em]">You</h1>
          <p className="mt-1 max-w-[40em] text-fg-3">
            {n ? `${count} number${n === 1 ? '' : 's'} you’re steering. Tap one to change its goal.` : 'Pick a number to steer, and we’ll show where it’s headed.'}
          </p>
        </div>
        <Link to="/data" className="text-sm font-medium text-fg-2 hover:text-fg">All your numbers →</Link>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 gap-3.5 md:grid-cols-2">
          {[0, 1].map((i) => <div key={i} className="h-[320px] animate-pulse rounded-xl border border-line bg-raised" />)}
        </div>
      ) : (
        <>
          {n > 0 && (
            <div className="grid grid-cols-1 gap-3.5 md:grid-cols-2">
              {cards.map((card) => {
                const open = openKey === card.metric_key;
                const target = targetByKey.get(card.metric_key);
                return (
                  <div key={card.metric_key} className={`min-w-0 ${open ? 'md:col-span-2' : ''}`}>
                    <SteerCard card={card} metric={byKey.get(card.metric_key)} open={open}
                      onOpen={(k) => setOpenKey((cur) => (cur === k ? null : k))} />
                    {open && target && (
                      <div className="mt-2">
                        <DialTargetEditor target={target} onSaved={handleTargetSaved}
                          onRemove={() => setPin(card.metric_key, false)} />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
          <AddNumber available={available} busy={busy} onAdd={(k) => setPin(k, true)} />
        </>
      )}

      <TryThis />
      <SupplementIdeasRow />
    </div>
  );
};

export default YouPage;
